import mori from 'mori';
import composeFootnotes from './composeFootnotes';
import getActiveFootnotes from './getActiveFootnotes';
import getDistributions from './getDistributions';
import getLocations from './getLocations';
import getRelevantRows from './getRelevantRows';
import getTimeframes from './getTimeframes';
import labelFootnotes from './labelFootnotes';
import sortRows from './sortRows';

/**
 * Get footnotes for the current "view"
 *
 * If the tab is table, the footnotes need to be:
 *   #{year, #{parent-column, child-column}, location}
 *
 * If the tab is map or graph, the footnotes need to be:
 *   #{#{parent-column, child-column}, location}
 *
 * Parent-column or child-column can be null
 */
export default function getFootnotes(
  sheetData,
  metaData,
  options,
  filteredData,
  combinedFilters
) {
  const footnotes = mori.getIn(
    sheetData.config,
    ['footnotes', 'footnotes'],
    false
  );

  // Get the base context and use it if it exists.
  // This is needed to accurately calculate weights for
  // footnote labels
  const baseContext = mori.getIn(
    sheetData.config,
    ['globalRules', 'Context'],
    []
  )[0] || 'Location';

  const isGeoMulti = mori.getIn(
    sheetData.config,
    ['globalRules', 'Geomulti'],
    false
  );

  // If there are no footnotes, just return an empty hashMap
  if (!footnotes) {
    return mori.hashMap();
  }
  // Get the timeframes, locations, and distributions
  const timeframes = getTimeframes(filteredData);
  const locations = getLocations(combinedFilters[0]);
  const distributions = getDistributions(combinedFilters[1]);
  // Using the actives above, get the footnotes
  const activeFootnotes = getActiveFootnotes(
    footnotes,
    timeframes,
    locations,
    distributions
  );
  // Is a trend graph when there is more than two timeframes (timeframe & any)
  const isTrendGraph = mori.count(timeframes) > 2;
  // Pull out active locations (locations with footnotes);
  const activeLocations = mori.keys(activeFootnotes);

  const mergedLocationsDistributions =
    mori.map((note) => {
      const geography = mori.first(note);
      const distribution = mori.first(mori.first(mori.first(mori.rest(note))));
      const distributionValue = mori.get(
        mori.first(mori.first(
          mori.vals(
            mori.first(
              mori.rest(note)
            )
          ))
        ),
        'value',
        null
      );

      if (distributionValue) {
        return mori.hashMap(
          'geography', geography,
          'distribution', distribution,
          'distributionValue', distributionValue
        );
      }

      return mori.hashMap(
        'geography', geography,
        'distribution', distribution,
      );
    }, activeFootnotes);

  // Pull out rows of data that have active locations
  const relevantRows = getRelevantRows(
    filteredData,
    activeLocations,
    mergedLocationsDistributions
  );
  // Sort the data, to match the table sort
  const sortedRows = mori.reduce(
    (acc, row) => mori.conj(
      acc,
      mori.hashMap(mori.first(row), mori.last(row))
    ),
    mori.hashMap(),
    sortRows(
      options.sortModel || {},
      relevantRows,
      metaData.immutableHeaders,
      combinedFilters[3].type,
      baseContext,
      isGeoMulti
    )
  );
  // Compose the footnotes over the sorted data
  let composedFootnotes = mori.reduce(
    (acc, item) => mori.conj(acc, item),
    mori.hashMap(),
    mori.filter(
      (note) => !locations || mori.some(
        (loc) => loc ===
          mori.first(mori.rest(mori.first(mori.first(note)))),
        locations
      ),
      composeFootnotes(footnotes, timeframes, distributions)
    )
  );

  if (isTrendGraph) {
    // If trendGraph view we need to do a secondary compose
    composedFootnotes = mori.reduceKV(
      (acc, key, val) => mori.conj(
        acc,
        mori.hashMap(
          (
            (mori.last(mori.rest(key)) !== 'Location') ?
              mori.set([
                mori.first(key),
                mori.first(mori.rest(key)),
                mori.hashMap(
                  mori.first(key),
                  mori.last(mori.rest(key))
                ),
              ]) :
              key
          ),
          val
        )
      ),
      mori.hashMap(),
      composedFootnotes
    );
  }
  // Go though and label the footnotes
  const labeledFootnotes = labelFootnotes(
    composedFootnotes,
    sortedRows,
    metaData.immutableHeaders,
    timeframes,
    baseContext,
    isGeoMulti
  );

  // Filter (dedup), the footnotes for other useage.
  const filteredFootnotes = mori.reduceKV(
    (acc, key, val) => mori.into(acc, val),
    mori.set(),
    labeledFootnotes
  );

  // Uncomment and add `import { log } from '../../util/util';` at top to debug
  // log('IND: timeframes', timeframes);
  // log('IND: locations', locations);
  // log('IND: distributions', distributions);
  // log('IND: activeFootnotes', activeFootnotes);
  // log('IND: activeLocations', activeLocations);
  // log('IND: relevantRows', relevantRows);
  // log('IND: sortedRows', sortedRows);
  // log('IND: composedFootnotes', composedFootnotes);
  // log('IND: labeledFootnotes', labeledFootnotes);

  return {
    labeledFootnotes,
    filteredFootnotes,
  };
}



/** WEBPACK FOOTER **
 ** ../~/eslint-loader!./js/services/getFootnotes/index.js
 **/